import { QueryClient } from '@tanstack/react-query';

// Define cache times based on data volatility
export const CACHE_TIMES = {
  // Static data (rarely changes)
  STATIC: {
    staleTime: 60 * 60 * 1000, // 1 hour
    cacheTime: 120 * 60 * 1000, // 2 hours
  },
  // Reference data (changes occasionally)
  REFERENCE: {
    staleTime: 30 * 60 * 1000, // 30 minutes
    cacheTime: 60 * 60 * 1000, // 1 hour
  },
  // Dynamic data (changes frequently)
  DYNAMIC: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
  },
  // Real-time data (always fresh)
  REALTIME: {
    staleTime: 0, // Always stale
    cacheTime: 1 * 60 * 1000, // 1 minute
  },
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        // Retry up to 2 times for other errors
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      // Default to dynamic data timing
      staleTime: CACHE_TIMES.DYNAMIC.staleTime,
      cacheTime: CACHE_TIMES.DYNAMIC.cacheTime,
      refetchInterval: false, // No automatic refetching by default
      refetchIntervalInBackground: false,
    },
    mutations: {
      retry: (failureCount, error: any) => {
        // Don't retry mutations on client errors
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        // Retry once for network/server errors
        return failureCount < 1;
      },
      retryDelay: 1000,
      // Optimistic updates configuration
      onError: (error, variables, context: any) => {
        // Rollback optimistic updates on error
        if (context?.rollback) {
          context.rollback();
        }
      },
    },
  },
});

// Helper to invalidate related queries
export function invalidateRelatedQueries(queryKeys: string[], exact = false) {
  queryKeys.forEach((key) => {
    queryClient.invalidateQueries({
      queryKey: [key],
      exact,
    });
  });
}

// Helper to prefetch data
export async function prefetchQuery(queryKey: any[], queryFn: () => Promise<any>, options?: any) {
  return queryClient.prefetchQuery({
    queryKey,
    queryFn,
    ...options,
  });
}

// Helper to set query data optimistically
export function setOptimisticData<T>(queryKey: any[], updater: (old: T | undefined) => T) {
  return queryClient.setQueryData(queryKey, updater);
}

// Helper to batch invalidations
export function batchInvalidate(invalidations: Array<{ queryKey: any[]; exact?: boolean }>) {
  queryClient.cancelQueries();

  invalidations.forEach(({ queryKey, exact }) => {
    queryClient.invalidateQueries({
      queryKey,
      exact,
    });
  });
}
